// Database Manager - Gerçek veritabanı için hazırlık sistemi
// LocalStorage ve API arası geçiş için abstraction layer

import { propertyMatchesFilters } from '../utils/propertyFilterEngine';

class DatabaseManager {
    constructor() {
        this.mode = 'localStorage'; // 'localStorage' veya 'api'
        this.apiConfig = {
            baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
            timeout: 10000,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.getToken()}`
            }
        };
        this.localStorageKeys = {
            properties: 'bi_gayrimenkul_properties',
            users: 'bi_gayrimenkul_users',
            settings: 'bi_gayrimenkul_settings',
            zipImports: 'bi_gayrimenkul_zip_imports'
        };
    }

    // Token yönetimi
    getToken() {
        return localStorage.getItem('auth_token') || '';
    }

    setToken(token) {
        localStorage.setItem('auth_token', token);
        this.apiConfig.headers.Authorization = `Bearer ${token}`;
    }

    // Veritabanı modunu değiştir
    setMode(mode) {
        if (['localStorage', 'api'].includes(mode)) {
            this.mode = mode;
            console.log(`Database mode changed to: ${mode}`);
        } else {
            throw new Error('Invalid database mode. Use "localStorage" or "api"');
        }
    }

    // API istekleri için genel fonksiyon
    async apiRequest(endpoint, method = 'GET', data = null) {
        if (this.mode !== 'api') {
            throw new Error('API mode is not enabled');
        }

        const url = `${this.apiConfig.baseUrl}${endpoint}`;
        const config = {
            method,
            headers: this.apiConfig.headers,
            timeout: this.apiConfig.timeout
        };

        if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
            config.body = JSON.stringify(data);
        }

        try {
            const response = await fetch(url, config);

            if (!response.ok) {
                throw new Error(`API Error: ${response.status} - ${response.statusText}`);
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('API Request Error:', error);
            throw error;
        }
    }

    // ==========================================
    // PROPERTIES (EMLAK İLANLARI) YÖNETİMİ
    // ==========================================

    async getProperties() {
        if (this.mode === 'localStorage') {
            return JSON.parse(localStorage.getItem(this.localStorageKeys.properties) || '[]');
        } else {
            return await this.apiRequest('/properties');
        }
    }

    async addProperty(property) {
        const propertyData = {
            ...property,
            id: property.id || Date.now(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        if (this.mode === 'localStorage') {
            const properties = await this.getProperties();
            properties.push(propertyData);
            localStorage.setItem(this.localStorageKeys.properties, JSON.stringify(properties));
            return propertyData;
        } else {
            return await this.apiRequest('/properties', 'POST', propertyData);
        }
    }

    async updateProperty(id, updatedProperty) {
        const propertyData = {
            ...updatedProperty,
            id,
            updatedAt: new Date().toISOString()
        };

        if (this.mode === 'localStorage') {
            const properties = await this.getProperties();
            const index = properties.findIndex(p => p.id === id);
            if (index !== -1) {
                properties[index] = { ...properties[index], ...propertyData };
                localStorage.setItem(this.localStorageKeys.properties, JSON.stringify(properties));
                return properties[index];
            }
            return null;
        } else {
            return await this.apiRequest(`/properties/${id}`, 'PUT', propertyData);
        }
    }

    async deleteProperty(id) {
        if (this.mode === 'localStorage') {
            const properties = await this.getProperties();
            const filteredProperties = properties.filter(p => p.id !== id);
            localStorage.setItem(this.localStorageKeys.properties, JSON.stringify(filteredProperties));
            return true;
        } else {
            await this.apiRequest(`/properties/${id}`, 'DELETE');
            return true;
        }
    }

    async getPropertyById(id) {
        if (this.mode === 'localStorage') {
            const properties = await this.getProperties();
            return properties.find(
                (p) => String(p.id) === String(id)
                    || (p.propertyId != null && String(p.propertyId) === String(id))
            ) || null;
        } else {
            return await this.apiRequest(`/properties/${id}`);
        }
    }

    // Filtreleme ve arama
    async searchProperties(filters = {}) {
        const properties = await this.getProperties();
        return properties.filter((property) => propertyMatchesFilters(property, {
            searchTerm: filters.searchTerm,
            propertyType: filters.propertyType,
            roomCount: filters.roomCount,
            minPrice: filters.minPrice,
            maxPrice: filters.maxPrice,
            minSize: filters.minSize,
            maxSize: filters.maxSize,
            buildingAge: filters.buildingAge,
            floor: filters.floor,
            heating: filters.heating,
            locationFilter: filters.location || filters.locationFilter
        }));
    }

    // ==========================================
    // ZIP IMPORT YÖNETİMİ
    // ==========================================

    async saveZipImport(zipImportData) {
        const importRecord = {
            id: Date.now(),
            fileName: zipImportData.fileName,
            fileSize: zipImportData.fileSize,
            propertiesFound: zipImportData.propertiesFound,
            propertiesAdded: zipImportData.propertiesAdded,
            imagesFound: zipImportData.imagesFound,
            status: 'completed',
            importDate: new Date().toISOString(),
            propertyIds: zipImportData.propertyIds || []
        };

        if (this.mode === 'localStorage') {
            const imports = JSON.parse(localStorage.getItem(this.localStorageKeys.zipImports) || '[]');
            imports.push(importRecord);
            localStorage.setItem(this.localStorageKeys.zipImports, JSON.stringify(imports));
            return importRecord;
        } else {
            return await this.apiRequest('/zip-imports', 'POST', importRecord);
        }
    }

    async getZipImports() {
        if (this.mode === 'localStorage') {
            return JSON.parse(localStorage.getItem(this.localStorageKeys.zipImports) || '[]');
        } else {
            return await this.apiRequest('/zip-imports');
        }
    }

    // ==========================================
    // KULLANICI YÖNETİMİ
    // ==========================================

    async getUsers() {
        if (this.mode === 'localStorage') {
            return JSON.parse(localStorage.getItem(this.localStorageKeys.users) || '[]');
        } else {
            return await this.apiRequest('/users');
        }
    }

    async authenticateUser(username, password) {
        if (this.mode === 'localStorage') {
            const users = await this.getUsers();
            const user = users.find(u => u.username === username && u.password === password);
            if (user) {
                const token = `local_${Date.now()}_${user.username}`;
                this.setToken(token);
                return { user: { ...user, password: undefined }, token };
            }
            return null;
        } else {
            const result = await this.apiRequest('/auth/login', 'POST', { username, password });
            if (result.token) {
                this.setToken(result.token);
            }
            return result;
        }
    }

    // ==========================================
    // VERİTABANI SIENKRONIZASYONU
    // ==========================================

    async syncToAPI() {
        if (this.mode === 'api') {
            console.log('Already in API mode');
            return;
        }

        try {
            // LocalStorage'dan verileri al
            const properties = await this.getProperties();
            const zipImports = await this.getZipImports();

            // API'ye geç
            this.setMode('api');

            // Verileri API'ye gönder
            const syncResults = {
                properties: [],
                zipImports: [],
                errors: []
            };

            // Properties sync
            for (const property of properties) {
                try {
                    const result = await this.addProperty(property);
                    syncResults.properties.push(result);
                } catch (error) {
                    syncResults.errors.push({ type: 'property', data: property, error: error.message });
                }
            }

            // ZIP Imports sync
            for (const zipImport of zipImports) {
                try {
                    const result = await this.saveZipImport(zipImport);
                    syncResults.zipImports.push(result);
                } catch (error) {
                    syncResults.errors.push({ type: 'zipImport', data: zipImport, error: error.message });
                }
            }

            console.log('Sync to API completed:', syncResults);
            return syncResults;

        } catch (error) {
            // Hata durumunda localStorage'a geri dön
            this.setMode('localStorage');
            throw error;
        }
    }

    // ==========================================
    // YARDIMCI FONKSİYONLAR
    // ==========================================

    async getStatistics() {
        const properties = await this.getProperties();
        const zipImports = await this.getZipImports();

        return {
            totalProperties: properties.length,
            totalZipImports: zipImports.length,
            propertyTypes: this.groupBy(properties, 'propertyType'),
            locations: this.groupBy(properties, 'location'),
            lastImport: zipImports.length > 0 ? zipImports[zipImports.length - 1] : null,
            averagePrice: properties.length > 0 ?
                properties.reduce((sum, p) => sum + (p.price || 0), 0) / properties.length : 0
        };
    }

    groupBy(array, key) {
        return array.reduce((groups, item) => {
            const group = item[key] || 'Diğer';
            groups[group] = (groups[group] || 0) + 1;
            return groups;
        }, {});
    }

    // Veritabanı durumu kontrolü
    async healthCheck() {
        if (this.mode === 'localStorage') {
            return {
                status: 'ok',
                mode: 'localStorage',
                data: {
                    properties: (await this.getProperties()).length,
                    users: (await this.getUsers()).length,
                    zipImports: (await this.getZipImports()).length
                }
            };
        } else {
            try {
                return await this.apiRequest('/health');
            } catch (error) {
                return {
                    status: 'error',
                    mode: 'api',
                    error: error.message
                };
            }
        }
    }
}

// Singleton instance
const databaseManager = new DatabaseManager();

export default databaseManager;
